import { defineStore } from 'pinia';
import apiClient from '@/util/apiClient.ts';

export interface PatternsDTO {
	patternNumber: number | null;
	scope?: 'Group' | 'Client' | null;
	group?: { groupNumber: number; name?: string } | null;
	client?: { clientNumber: number; name?: string } | null;
	groupNumber?: number | null;
	clientNumber?: number | null;
	turnbackTime: string | null;
	dailyCenters: number | null;
	dailyARs: number | null;
	sameTeam: string | null;
	dailyAssignments: number | null;
	completedProfile: boolean | null;
}

export interface SavePatternsRequest {
	groupNumber: number;
	clientNumber?: number | null;
	scope: 'Group' | 'Client';
	turnbackTime: string | null;
	dailyCenters: number | null;
	dailyARs: number | null;
	sameTeam: string | null;
	dailyAssignments: number | null;
	completedProfile: boolean | null;
}

interface PatternsState {
	patternsByGroup: Record<number, PatternsDTO | null | undefined>;
	patternsByClient: Record<number, PatternsDTO | null | undefined>;
}

function clientPatternsFromResponse(payload: unknown): PatternsDTO[] {
	if (Array.isArray(payload)) return payload as PatternsDTO[];
	if (!payload || typeof payload !== 'object') return [];
	const record = payload as Record<string, unknown>;
	for (const key of ['patterns', 'data', 'results', 'content']) {
		if (Array.isArray(record[key])) return record[key] as PatternsDTO[];
	}
	if ('patternNumber' in record || 'clientNumber' in record || 'client' in record) return [record as unknown as PatternsDTO];
	return Object.entries(record).flatMap(([clientNumber, pattern]) => {
		if (!pattern || typeof pattern !== 'object') return [];
		const numericClientNumber = Number(clientNumber);
		return [
			{
				...(pattern as PatternsDTO),
				clientNumber: (pattern as PatternsDTO).client?.clientNumber ?? (pattern as PatternsDTO).clientNumber ?? (Number.isSafeInteger(numericClientNumber) ? numericClientNumber : null)
			}
		];
	});
}

export const usePatternsStore = defineStore('patternsStore', {
	state: (): PatternsState => ({ patternsByGroup: {}, patternsByClient: {} }),
	actions: {
		async fetchGroupPattern(groupNumber: number): Promise<PatternsDTO | null> {
			try {
				const response = await apiClient.get<PatternsDTO>(`/patterns/group/${groupNumber}`);
				this.patternsByGroup[groupNumber] = response.data;
				return response.data;
			} catch (error: unknown) {
				const status = (error as { response?: { status?: number } } | undefined)?.response?.status;
				if (status === 404) {
					this.patternsByGroup[groupNumber] = null;
					return null;
				}
				throw error;
			}
		},

		async fetchClientPattern(clientNumber: number): Promise<PatternsDTO | null> {
			try {
				const response = await apiClient.get<PatternsDTO>(`/patterns/client/${clientNumber}`);
				this.patternsByClient[clientNumber] = response.data;
				return response.data;
			} catch (error: unknown) {
				const status = (error as { response?: { status?: number } } | undefined)?.response?.status;
				if (status === 404) {
					this.patternsByClient[clientNumber] = null;
					return null;
				}
				throw error;
			}
		},

		async fetchClientPatterns(clientNumbers: number[]): Promise<PatternsDTO[]> {
			const ids = [...new Set(clientNumbers.map(Number).filter((clientNumber) => Number.isSafeInteger(clientNumber) && clientNumber > 0))];
			if (!ids.length) return [];

			const response = await apiClient.post<unknown>('/patterns/client', ids);
			const patterns = clientPatternsFromResponse(response.data);
			for (const pattern of patterns) {
				const clientNumber = Number(pattern.client?.clientNumber ?? pattern.clientNumber);
				if (Number.isSafeInteger(clientNumber) && clientNumber > 0) this.patternsByClient[clientNumber] = pattern;
			}
			return patterns;
		},

		async savePattern(patternNumber: number | null | undefined, request: SavePatternsRequest): Promise<PatternsDTO> {
			const updateRequest = {
				scope: request.scope,
				turnbackTime: request.turnbackTime,
				dailyCenters: request.dailyCenters,
				dailyARs: request.dailyARs,
				sameTeam: request.sameTeam,
				dailyAssignments: request.dailyAssignments,
				completedProfile: request.completedProfile
			};
			const response = patternNumber ? await apiClient.patch<PatternsDTO>(`/patterns/${patternNumber}`, updateRequest) : await apiClient.post<PatternsDTO>('/patterns/save', request);
			const groupId = response.data.group?.groupNumber ?? response.data.groupNumber ?? request.groupNumber;
			const clientId = response.data.client?.clientNumber ?? response.data.clientNumber ?? request.clientNumber;
			if (groupId && !clientId) this.patternsByGroup[groupId] = response.data;
			if (clientId) this.patternsByClient[clientId] = response.data;
			return response.data;
		},

		async saveGroupPattern(groupNumber: number, request: Omit<SavePatternsRequest, 'groupNumber' | 'clientNumber'>): Promise<PatternsDTO> {
			return this.savePattern(this.patternsByGroup[groupNumber]?.patternNumber, { groupNumber, ...request });
		},

		async saveClientPattern(groupNumber: number, clientNumber: number, request: Omit<SavePatternsRequest, 'groupNumber' | 'clientNumber'>): Promise<PatternsDTO> {
			return this.savePattern(this.patternsByClient[clientNumber]?.patternNumber, { groupNumber, clientNumber, ...request });
		}
	}
});
